/* ============================================================
   ott-warranty.js — NewJaisa PDP Offer Widget v8.1
   Behaviour only. No styling, no markup generation.

   v8.1: the countdown driver has been REMOVED from this file.
   The theme already runs a global countdown in theme.liquid
   that binds to .countdown-timer-2 / data-launch-date and
   writes into .days / .hours / .minutes / .seconds. The widget
   markup uses exactly that contract, so the global script
   drives it. Nothing here touches the timer.

   THE COUPON FLOW IS UNCHANGED. Every guarantee still holds:
   · Never touches GET /discount/<CODE>.
   · Requests fire ONLY on explicit Apply / Remove clicks, plus
     one silent re-sync for customers whose localStorage proves
     they previously clicked Apply. Visitors who never applied
     generate ZERO requests.
   · The ONLY write is POST /cart/update.js with a body of
     exactly one key: { discount: "..." }. No `updates`, no
     `quantity`, no `id`, no line items — quantities and
     products CANNOT be modified by this code.
   · No listeners on Add to Cart, Buy Now, checkout, quantity
     selectors, upsells or recommendations. No redirects.
   The code is written to the CART, not a URL cookie, so it
   carries through Add to cart, Buy it now, the cart drawer
   and checkout exactly as before.
   ============================================================ */

(function () {
  'use strict';

  var LS_KEY = 'njv2_coupon_applied';
  var COUPON = 'NJ-6MTH';

  var resynced = false;
  var syncCallbacks = [];
  var variantListenersBound = false;

  /* ── Cart AJAX API helpers ───────────────────────────── */
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function getAppliedCodes(cart) {
    var codes = [];
    try {
      if (cart && Array.isArray(cart.discount_codes)) {
        cart.discount_codes.forEach(function (d) {
          if (d && d.code) codes.push(String(d.code));
        });
      }
      if (!codes.length && cart && Array.isArray(cart.cart_level_discount_applications)) {
        cart.cart_level_discount_applications.forEach(function (d) {
          if (d && d.title) codes.push(String(d.title));
        });
      }
    } catch (e) {}
    return codes;
  }

  function isOurCode(code) {
    return String(code).trim().toUpperCase() === COUPON.toUpperCase();
  }

  /* The ONLY write request in this file. */
  function setCartDiscounts(codes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    });
  }

  function applyDiscountToCart() {
    return getCart().then(function (cart) {
      var codes = getAppliedCodes(cart);
      if (codes.some(isOurCode)) return;
      codes.push(COUPON);
      return setCartDiscounts(codes);
    }).catch(function () {});
  }

  function removeDiscountFromCart() {
    return getCart().then(function (cart) {
      var codes  = getAppliedCodes(cart);
      var others = codes.filter(function (c) { return !isOurCode(c); });
      if (others.length !== codes.length) return setCartDiscounts(others);
    }).catch(function () {});
  }

  function resyncAppliedCustomer() {
    if (resynced) return;
    resynced = true;
    getCart().then(function (cart) {
      var codes = getAppliedCodes(cart);
      if (!codes.some(isOurCode)) {
        codes.push(COUPON);
        return setCartDiscounts(codes);
      }
    }).catch(function () {});
  }

  /* ── Lazy panel images ───────────────────────────────── */
  function loadPanelImg(inner) {
    var img = inner.querySelector('img[data-src]');
    if (img && !img.getAttribute('src')) {
      img.setAttribute('src', img.getAttribute('data-src'));
    }
  }

  /* ── Tile expand / collapse ──────────────────────────── */
  function initTileClicks(wrap) {
    if (!wrap || wrap.dataset.njcpTileInit === '1') return;
    wrap.dataset.njcpTileInit = '1';

    var panel = wrap.querySelector('.njcp-img-panel');
    var tiles = Array.prototype.slice.call(
      wrap.querySelectorAll('.njcp-tile-btn[data-njcp-target]')
    );
    if (!panel || !tiles.length) return;

    var inners  = Array.prototype.slice.call(panel.querySelectorAll('.njcp-img-inner'));
    var openKey = null;

    function setTileState(tile, isOpen) {
      tile.classList.toggle('njcp-tile-open', isOpen);
      tile.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    function closePanel() {
      panel.classList.remove('njcp-panel-open');
      tiles.forEach(function (t) { setTileState(t, false); });
      inners.forEach(function (el) { el.hidden = true; });
      openKey = null;
    }

    function openPanel(key, tile) {
      tiles.forEach(function (t) { setTileState(t, t === tile); });
      var target = null;
      inners.forEach(function (el) {
        var match = el.getAttribute('data-njcp-type') === key;
        el.hidden = !match;
        if (match) target = el;
      });
      if (!target) { closePanel(); return; }
      loadPanelImg(target);
      panel.classList.add('njcp-panel-open');
      openKey = key;
    }

    tiles.forEach(function (tile) {
      tile.addEventListener('click', function (e) {
        e.preventDefault();
        var key = tile.getAttribute('data-njcp-target');
        if (openKey === key) { closePanel(); } else { openPanel(key, tile); }
      });
    });
  }

  /* ── One set of variant listeners for the page ───────── */
  function runAllSyncs() {
    for (var i = 0; i < syncCallbacks.length; i++) {
      try { syncCallbacks[i](); } catch (e) {}
    }
  }

  function bindVariantListenersOnce() {
    if (variantListenersBound) return;
    variantListenersBound = true;

    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') runAllSyncs();
    }, { passive: true });

    ['variant:changed', 'variantChange', 'variant-change'].forEach(function (ev) {
      document.addEventListener(ev, runAllSyncs, { passive: true });
    });
  }

  /* ── Coupon state ────────────────────────────────────── */
  function initCoupon(wrap) {
    if (!wrap || wrap.dataset.njcpInit === '1') return;
    wrap.dataset.njcpInit = '1';

    var pend      = wrap.querySelector('.njcp-pend');
    var done      = wrap.querySelector('.njcp-done');
    var applyBtn  = wrap.querySelector('.njcp-apply-btn');
    var removeBtn = wrap.querySelector('.njcp-remove-btn');

    if (!pend || !done || !applyBtn || !removeBtn) return;

    function showApplied() {
      pend.hidden = true;
      done.hidden = false;
      try { localStorage.setItem(LS_KEY, COUPON); } catch (e) {}
    }

    function showPending() {
      done.hidden = true;
      pend.hidden = false;
      try { localStorage.removeItem(LS_KEY); } catch (e) {}
    }

    function syncState() {
      var applied = false;
      try { applied = localStorage.getItem(LS_KEY) === COUPON; } catch (e) {}
      if (applied) { pend.hidden = true; done.hidden = false; }
      else         { done.hidden = true; pend.hidden = false; }
    }

    applyBtn.addEventListener('click', function (e) {
      e.preventDefault();
      showApplied();
      applyDiscountToCart();
    });

    removeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      showPending();
      removeDiscountFromCart();
    });

    syncState();
    syncCallbacks.push(syncState);
    bindVariantListenersOnce();

    try {
      if (localStorage.getItem(LS_KEY) === COUPON) resyncAppliedCustomer();
    } catch (e) {}
  }

  /* ── Defer tile wiring to first interaction ──────────── */
  function deferTileInit(wrap) {
    function onInteract() {
      initTileClicks(wrap);
      wrap.removeEventListener('pointerdown', onInteract);
      wrap.removeEventListener('focusin',     onInteract);
    }
    wrap.addEventListener('pointerdown', onInteract, { passive: true });
    wrap.addEventListener('focusin',     onInteract, { passive: true });
  }

  /* ── Bootstrap ───────────────────────────────────────── */
  function bootstrap() {
    var wraps = document.querySelectorAll('.njcp-wrap');
    if (!wraps.length) return;

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          initCoupon(entry.target);
          deferTileInit(entry.target);
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '200px 0px' });

      Array.prototype.forEach.call(wraps, function (w) { io.observe(w); });
    } else {
      Array.prototype.forEach.call(wraps, function (w) {
        initCoupon(w);
        initTileClicks(w);
      });
    }
  }

  bootstrap();

  window.__initPromoCard = function (root) {
    (root || document).querySelectorAll('.njcp-wrap').forEach(function (w) {
      initCoupon(w);
      initTileClicks(w);
    });
  };

})();
